'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import MultipleSelector from '../../components/MultipleSelector';
import { MultiSelectCheckboxes } from '../../components/MultiSelect';
import { MULTI_SELECT_CLEARED } from '../store';
import { useTableFilters } from '../useTableFilters';
import type { ReactNode } from 'react';
import type { FormOption } from '../../types';
import type { BaseTableFilterProps, FilterOption } from './types';
export interface MultiSelectTableFilterProps extends BaseTableFilterProps {
  filterOptions: readonly FilterOption[];
  searchParam: string;
  variant: 'multiSelect';
  /** 'checkbox' = popover with checkboxes (default), 'search' = cmdk typeahead combobox */
  style?: 'checkbox' | 'search';
  defaultValue?: readonly string[];
  placeholder?: string;
  clearable?: boolean;
  /** Custom renderer for option labels in the checkbox dropdown (e.g., colored badges) */
  renderLabel?: (option: FilterOption) => ReactNode;
  /** Classes for the dropdown list (`search` style only), e.g. to size it to content. */
  dropdownClassName?: string;
  groups?: never;
  value?: never;
  fromDateParam?: never;
  toDateParam?: never;
}
function parseUrlValues(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').filter(Boolean);
}
export function MultiSelectTableFilter(t0) {
  const $ = _c(40);
  const {
    className,
    label,
    searchParam,
    searchParamsToClearOnChange,
    filterOptions,
    defaultValue,
    placeholder,
    style: t1,
    renderLabel,
    dropdownClassName,
    filterType: t2
  } = t0;
  const style = t1 === undefined ? "search" : t1;
  const filterType = t2 === undefined ? "rsc" : t2;
  const searchParams = useSearchParams();
  const {
    updateMultiSelectFlatFilter
  } = useTableFilters(filterType);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [selectedValues, setSelectedValues] = useState(t3);
  let t4;
  let t5;
  if ($[1] !== defaultValue || $[2] !== searchParam || $[3] !== searchParams) {
    t4 = () => {
      const urlValue = searchParams?.get(searchParam) ?? null;
      if (urlValue === MULTI_SELECT_CLEARED) {
        setSelectedValues([]);
      } else {
        if (urlValue) {
          setSelectedValues(parseUrlValues(urlValue));
        } else {
          if (defaultValue) {
            setSelectedValues([...defaultValue]);
          } else {
            setSelectedValues([]);
          }
        }
      }
    };
    t5 = [searchParams, searchParam, defaultValue];
    $[1] = defaultValue;
    $[2] = searchParam;
    $[3] = searchParams;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== defaultValue || $[7] !== searchParam || $[8] !== searchParamsToClearOnChange || $[9] !== updateMultiSelectFlatFilter) {
    t6 = function handleChange(values) {
      setSelectedValues(values);
      updateMultiSelectFlatFilter(searchParam, values, searchParamsToClearOnChange, defaultValue);
    };
    $[6] = defaultValue;
    $[7] = searchParam;
    $[8] = searchParamsToClearOnChange;
    $[9] = updateMultiSelectFlatFilter;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleChange = t6;
  let formOptions;
  let t7;
  if ($[11] !== filterOptions || $[12] !== selectedValues) {
    formOptions = filterOptions.map(_temp);
    let t8;
    if ($[15] !== selectedValues) {
      t8 = o_0 => selectedValues.includes(o_0.value);
      $[15] = selectedValues;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t7 = formOptions.filter(t8);
    $[11] = filterOptions;
    $[12] = selectedValues;
    $[13] = formOptions;
    $[14] = t7;
  } else {
    formOptions = $[13];
    t7 = $[14];
  }
  const selectedFormOptions = t7;
  if (style === "search") {
    const t8 = label ?? placeholder ?? searchParam;
    let t9;
    if ($[17] !== t8) {
      t9 = {
        "aria-label": t8
      };
      $[17] = t8;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== handleChange) {
      t10 = options => handleChange(options.map(_temp2));
      $[19] = handleChange;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== renderLabel) {
      t11 = renderLabel ? opt => renderLabel({
        value: opt.value,
        label: opt.label
      }) : undefined;
      $[21] = renderLabel;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== className || $[24] !== dropdownClassName || $[25] !== formOptions || $[26] !== placeholder || $[27] !== selectedFormOptions || $[28] !== t10 || $[29] !== t11 || $[30] !== t9) {
      t12 = <MultipleSelector className={className} defaultOptions={formOptions} dropdownClassName={dropdownClassName} inputProps={t9} onChange={t10} placeholder={placeholder} renderLabel={t11} value={selectedFormOptions} />;
      $[23] = className;
      $[24] = dropdownClassName;
      $[25] = formOptions;
      $[26] = placeholder;
      $[27] = selectedFormOptions;
      $[28] = t10;
      $[29] = t11;
      $[30] = t9;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    return t12;
  }
  const t8 = label ?? placeholder ?? searchParam;
  let t9;
  if ($[32] !== className || $[33] !== filterOptions || $[34] !== handleChange || $[35] !== placeholder || $[36] !== renderLabel || $[37] !== selectedValues || $[38] !== t8) {
    t9 = <MultiSelectCheckboxes className={className} ariaLabel={t8} onSelectionChange={handleChange} options={filterOptions} placeholder={placeholder} renderLabel={renderLabel} selectedValues={selectedValues} />;
    $[32] = className;
    $[33] = filterOptions;
    $[34] = handleChange;
    $[35] = placeholder;
    $[36] = renderLabel;
    $[37] = selectedValues;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  return t9;
}
function _temp2(o_1) {
  return o_1.value;
}
function _temp(o) {
  return {
    value: String(o.value),
    label: o.label
  };
}